import { UseClub, UseEmailMessage, UseGroups } from '@core/usecases';
import { EmailMessageEntity, GroupEntity, GroupParticipant, UserEntity } from '@core/entities';
import { untilDestroyed } from '@ngneat/until-destroy';
import { DropDownItem } from '@core/types/Dropdown.type';
import { EnumToKeyValueArray, GetHeadlineOption, GetKeycloakId, isMobile, isMobileView, removeNullAndUndefinedProperties } from '@core/utils';
import { ConfirmationDialogType, EmailFolder, EmailType, HeadlineType } from '@core/enums';
import { TranslateService } from '@ngx-translate/core';
import { inject } from '@angular/core';
import { NavMenuItem } from '@core/types';
import { emailMessagesFolders } from '@core/constants';
import { DialogService } from '@ngneat/dialog';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { HotToastService } from '@ngxpert/hot-toast';
import { Observable } from 'rxjs';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { FormService } from '@core/services';
import { FormBuilder, Validators } from '@angular/forms';
import { PERMISSIONS, PermissionService } from '@auth';
import { ButtonComponent } from '@shared/components/ui';
import { instanceToPlain } from 'class-transformer';
import { Router } from '@angular/router';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';

interface FileDetails {
  name: string;
  type: string;
}

export class EmailMessagesComposition extends FormService {
  filteredEmailMessages: EmailMessageEntity[] = [];
  selectedEmailMessage: EmailMessageEntity;

  searchInput: string = null;

  emailTypes: DropDownItem[] = [];
  selectedEmailType: EmailType = EmailType.PERSONAL; // Added Input decorator for mobile view as we have parent child relation based separate components

  emailFolders: DropDownItem[] = [];
  emailFoldersForMobile: DropDownItem[] = [];
  selectedEmailFolder: EmailFolder = EmailFolder.INBOX; // Added Input decorator for mobile view as we have parent child relation based separate components

  teamUsers: UserEntity[] = [];
  groups: GroupEntity[] = [];
  groupParticipants: GroupParticipant[] = [];

  selectedAttachment: File;

  fileDetails: FileDetails[] = [];

  replyTo: 'replyTo' | 'replyToAll';

  loadings = {
    teamUsers: true,
    groups: true,
  };

  protected readonly _bottomSheetService = inject(NgxBottomSheetService);
  protected readonly _dialogService = inject(DialogService);

  protected readonly emailMessagesFolders = emailMessagesFolders;
  protected readonly EmailType = EmailType;
  protected readonly EmailFolder = EmailFolder;

  protected readonly _translate = inject(TranslateService);
  protected readonly _permissionService = inject(PermissionService);

  private readonly _toastService = inject(HotToastService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _router = inject(Router);

  private readonly _useEmailMessages = new UseEmailMessage();
  private readonly _useClub = new UseClub();
  private readonly _useGroup = new UseGroups();
  private _emailMessages: EmailMessageEntity[] = [];

  constructor() {
    super();
    this.isLoading = true;
    this.emailTypes = EnumToKeyValueArray(EmailType, this._translate, false);

    if (GetHeadlineOption() === HeadlineType.ENTERPRISE) {
      this.emailTypes = this.emailTypes.map((type) => {
        if (type.value === EmailType.CLUB) {
          type.key = this._translate.instant('Corporate');
        }
        return type;
      });
    }

    this.emailFolders = EnumToKeyValueArray(EmailFolder, this._translate, false);
    this.emailFoldersForMobile = this.emailFolders.filter((item) => item.value !== EmailFolder.DRAFT);
    this._fetchTeamUsers();
  }

  get emailMessages() {
    return this._emailMessages;
  }

  set emailMessages(messages: EmailMessageEntity[]) {
    this._emailMessages = messages;
    this.filteredEmailMessages = messages;
  }

  get emailTypesForView(): DropDownItem[] {
    if (!this.canViewClubAndGroupMessages) {
      return this.emailTypes.filter((type) => type.value === EmailType.PERSONAL);
    }

    return this.emailTypes;
  }

  get emailTypesForForm(): DropDownItem[] {
    if (this._permissionService?.isUser() || this._permissionService?.isMember()) {
      return this.emailTypes.filter((type) => type.value !== EmailType.CLUB);
    }

    return this.emailTypes;
  }

  get canCreateMessage() {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_MESSAGE);
  }

  get canViewClubAndGroupMessages(): boolean {
    return !(this._permissionService?.isGuest() || this._permissionService?.isMemberLight() || this._permissionService?.isMember());
  }

  messageReply(text: 'replyTo' | 'replyToAll') {
    this._initReplyForm();
    this.replyTo = text;
  }

  selectEmailType(type: DropDownItem) {
    if (!this.canViewClubAndGroupMessages) return;
    this.selectedEmailType = type.value as EmailType;
    this.selectedEmailFolder = EmailFolder.INBOX;
    this.selectedEmailMessage = null;
    this.replyTo = null;
    this.isLoading = true;
    this._fetchMessages();
  }

  selectAllParticipants() {
    this.form.get('to').setValue(this.teamUsers.map((user) => user.keycloakId));
  }

  unSelectAll() {
    this.form.get('to').setValue([]);
  }

  searchMessage(event: any) {
    this.filteredEmailMessages = this.emailMessages.filter((message) => message?.user?.fullName.toLowerCase().includes(event.target.value.toLowerCase()));
    if (!event.target.value) {
      this.filteredEmailMessages = this.emailMessages;
    }
  }

  selectFolder(item: NavMenuItem | DropDownItem, isDropDown = false) {
    if (isDropDown) {
      this.selectedEmailFolder = (item as DropDownItem).value as EmailFolder;
    } else {
      this.selectedEmailFolder = (item as NavMenuItem).title as EmailFolder;
    }
    this.selectedEmailMessage = null;
    this.replyTo = null;
    this.isLoading = true;
    this._fetchMessages();
  }

  selectEmailMessage(message: EmailMessageEntity) {
    this.replyTo = null;
    this.selectedEmailMessage = message;
    if (this.selectedEmailType === EmailType.GROUP && this.selectedEmailMessage.kindId > 0 && this.selectedEmailMessage?.group) {
      this._fetchGroupUsers(this.selectedEmailMessage.kindId);
    }
    this._markMessageAsRead(message);
  }

  downloadAttachment() {
    if (!this.selectedEmailMessage) return;
    fetch(this.selectedEmailMessage.attachmentUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement('a');
        const objectURL = URL.createObjectURL(blob);
        link.href = objectURL;
        link.download = this.selectedEmailMessage.fileDetails;
        if (isMobile() || isMobileView()) {
          link.target = '_self';
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectURL);
      })
      .catch((error) => console.error('Download error:', error));
  }

  moveToTrash() {
    if (!this.selectedEmailMessage) return;
    this._dialogService
      .open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.MOVE_TO_TRASH,
        },
      })
      .afterClosed$.pipe(untilDestroyed(this))
      .subscribe({
        next: (yes) => {
          if (yes) {
            this._useEmailMessages
              .moveMessageToTrash(this.selectedEmailMessage.esDbId, this.selectedEmailMessage.id)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: (res) => {
                  if (res) {
                    this._toastService.success(this._translate.instant('Message moved to trash successfully'));
                    this.searchInput = null;
                    this.emailMessages = this._emailMessages.filter((m) => m.id !== this.selectedEmailMessage.id);
                    this.selectedEmailMessage = null;
                  } else {
                    this._toastService.error(this._translate.instant('Something went wrong'));
                  }
                },
                error: () => {
                  this._toastService.error(this._translate.instant('Something went wrong'));
                },
              });
          }
        },
      });
  }

  permanentlyDeleteMessage() {
    if (!this.selectedEmailMessage) return;
    let confirmDelete$;
    if (isMobileView()) {
      confirmDelete$ = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translate.instant('Message') + ' ' + this._translate.instant('permanently') + '?',
        },
      });
    } else {
      confirmDelete$ = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.PERMANENTLY_DELETE,
        },
      });
    }
    if (confirmDelete$) {
      confirmDelete$.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (yes) => {
          if (yes) {
            const service$ =
              this.selectedEmailMessage?.type === EmailFolder.DRAFT
                ? this._useEmailMessages.deleteDraft(this.selectedEmailMessage.id)
                : this._useEmailMessages.deleteMessage(this.selectedEmailMessage.esDbId);
            service$.pipe(untilDestroyed(this)).subscribe({
              next: (res) => {
                if (res) {
                  this._toastService.success(this._translate.instant('Delete Successful'));
                  this.searchInput = null;
                  this.emailMessages = this._emailMessages.filter((m) => m.id !== this.selectedEmailMessage.id);
                  this.selectedEmailMessage = null;
                } else {
                  this._toastService.error(this._translate.instant('Something went wrong'));
                }
              },
              error: () => {
                this._toastService.error(this._translate.instant('Something went wrong'));
              },
            });
          }
        },
      });
    }
  }

  starUnStarMessage() {
    if (!this.selectedEmailMessage) return;
    let service$: Observable<any>;
    let toastSuccessMessage: string;
    switch (this.selectedEmailFolder) {
      case EmailFolder.INBOX:
        service$ = this._useEmailMessages.moveMessageToStarred(this.selectedEmailMessage.esDbId, this.selectedEmailMessage.id);
        toastSuccessMessage = 'Message starred successfully';
        break;

      case EmailFolder.STARRED:
        service$ = this._useEmailMessages.moveMessageToInbox(this.selectedEmailMessage.esDbId, this.selectedEmailMessage.id);
        toastSuccessMessage = 'Message moved to inbox successfully';
        break;

      default:
        break;
    }

    if (service$) {
      service$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          if (res) {
            this._toastService.success(this._translate.instant(toastSuccessMessage));
            this.searchInput = null;
            this.emailMessages = this._emailMessages.filter((m) => m.id !== this.selectedEmailMessage.id);
            this.selectedEmailMessage = null;
          } else {
            this._toastService.error(this._translate.instant('Something went wrong'));
          }
        },
        error: () => {
          this._toastService.error(this._translate.instant('Something went wrong'));
        },
      });
    }
  }

  sendMessage(btnRef?: ButtonComponent, isDraft = false) {
    if (this.form.invalid) {
      this.markAllInvalidControlsAndScrollToFirst(this.form);
      return;
    }

    const formValues = this.form.value as EmailMessageEntity;

    if (isDraft) {
      formValues.messageType = 'draft';
    }

    const body = removeNullAndUndefinedProperties(instanceToPlain(new EmailMessageEntity(formValues)));

    const formData: FormData = this.jsonToFromData(body);

    formData.delete('to');

    if (formValues.to && formValues.to.length) {
      (formValues.to as unknown as string[]).forEach((kId, index) => {
        formData.append(`receiver_id[${index}]`, kId.toString());
      });
    }

    formData.delete('cc');

    if (formValues.cc && formValues.cc.length) {
      (formValues.cc as unknown as string[]).forEach((kId, index) => {
        formData.append(`cc[${index}]`, kId.toString());
      });
    }

    if (formValues.kind !== EmailType.GROUP) {
      formData.delete('kind_id');
    }

    if (this.selectedAttachment) {
      formData.append('file', this.selectedAttachment, this.selectedAttachment.name);
    } else if (this.selectedEmailMessage && this.selectedEmailMessage?.type === EmailFolder.DRAFT && this.selectedEmailMessage?.fileDetails) {
      formData.append('file', null);
      formData.append('file_details', this.selectedEmailMessage.fileDetails);
    }

    let service$: Observable<any>;
    switch (formValues.kind) {
      case EmailType.PERSONAL:
        service$ = this._useEmailMessages.sendPersonalMessage(formData);
        break;

      case EmailType.CLUB:
        service$ = this._useEmailMessages.sendClubMessage(formData);
        break;

      case EmailType.GROUP:
        service$ = this._useEmailMessages.sendGroupMessage(formData);
        break;

      default:
        break;
    }

    if (service$) {
      this.call(service$, btnRef).then((res) => {
        if (res) {
          const msg = isDraft ? 'Message saved as draft successfully' : 'Message sent successfully';
          this._toastService.success(this._translate.instant(msg));
          this.form.reset();

          if (this.selectedEmailMessage && this.selectedEmailMessage?.type === EmailFolder.DRAFT) {
            btnRef?.showLoading();
            this._useEmailMessages
              .deleteDraft(this.selectedEmailMessage.id)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: (res) => {
                  if (res) {
                    this.emailMessages = this._emailMessages.filter((m) => m.id !== this.selectedEmailMessage.id);
                    this.selectedEmailMessage = null;
                    this._reloadCurrentRoute();
                    if (isMobileView()) {
                      this._bottomSheetService.closeAll();
                    } else {
                      this._dialogService.closeAll();
                    }
                  }
                },
              });
          } else {
            this._reloadCurrentRoute();
            if (isMobileView()) {
              this._bottomSheetService.closeAll();
            } else {
              this._dialogService.closeAll();
            }
          }
        }
      });
    }
  }

  sendReplyMessage(btnRef?: ButtonComponent) {
    if (this.form.invalid) {
      this.markAllInvalidControlsAndScrollToFirst(this.form);
      return;
    }

    const formData: FormData = this.jsonToFromData(this.form.value);

    if (this.selectedAttachment) {
      formData.append('file', this.selectedAttachment, this.selectedAttachment.name);
    }

    let service$: Observable<any>;

    if (this.replyTo === 'replyTo') {
      service$ = this._useEmailMessages.replyToMessage(this.selectedEmailMessage.esDbId, formData);
    } else {
      service$ = this._useEmailMessages.replyToAllMessage(this.selectedEmailMessage.esDbId, formData);
    }

    if (service$) {
      this.call(service$, btnRef)
        .then((res) => {
          if (res) {
            this._toastService.success(this._translate.instant('Message sent successfully'));
            this.form.reset();
            this.replyTo = null;
            this._reloadCurrentRoute();
          } else {
            this._toastService.error(this._translate.instant('Something went wrong'));
          }
        })
        .catch(() => {
          this._toastService.error(this._translate.instant('Something went wrong'));
        });
    }
  }

  onFileSelected(event: any) {
    // const files: FileList = event.target.files;
    const file: File = event;
    const fileName: string = file.name;
    const fileType: string = this._getFileType(fileName);
    this.fileDetails = [{ name: fileName, type: fileType }];
    this.selectedAttachment = file;

    // For handling multiple files

    // for (let i = 0; i < files.length; i++) {
    //   const file: File = files[i];
    //   const fileName: string = file.name;
    //   const fileType: string = this.getFileType(fileName);
    //   this.fileDetails.push({ name: fileName, type: fileType });
    // }
  }

  markEmailSectionAsRead() {
    this._useEmailMessages.markEmailMessagesAsRead().pipe(untilDestroyed(this)).subscribe();
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _fetchPersonalInboxMessages() {
    this._useEmailMessages
      .getAllPersonalInboxMessages()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (messages) => {
          this.emailMessages = messages;
        },
        complete: () => {
          this._processUsersForCCAndTO(this.emailMessages, this.teamUsers);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this._toastService.error(this._translate.instant('Something went wrong'));
        },
      });
  }

  protected _initForm() {
    this._fetchGroups();

    this.form = this._formBuilder.group({
      kind: ['', [Validators.required]],
      kindId: [null],
      to: [null, Validators.required],
      cc: [null],
      subject: ['', [Validators.required]],
      content: ['', [Validators.required]],
      type: ['text'],
      senderId: [GetKeycloakId()],
      messageType: [EmailFolder.INBOX],
    });

    if (!this.canCreateMessage || this._permissionService.isMember()) {
      this.emailTypes = this.emailTypes.filter((type) => type.value !== EmailType.CLUB);
    }

    this._bindFormValueChanges();
  }

  protected _patchForm() {
    this.form.patchValue({
      kind: this.selectedEmailMessage.kind,
      kindId: this.selectedEmailMessage.kindId,
      to: this.selectedEmailMessage.to,
      cc: this.selectedEmailMessage.cc,
      subject: this.selectedEmailMessage.subject,
      content: this.selectedEmailMessage.content,
      type: this.selectedEmailMessage.type,
      senderId: this.selectedEmailMessage.senderId,
      messageType: this.selectedEmailMessage.messageType,
    });
  }

  private _bindFormValueChanges() {
    this.form
      .get('kind')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (kind: EmailType) => {
          if (kind === EmailType.GROUP) {
            this.form.get('kindId').setValidators([Validators.required]);
            this.form.get('to').clearValidators();
          } else {
            this.form.get('kindId').setValue(null);
            this.form.get('kindId').clearValidators();
            this.form.get('to').setValidators([Validators.required]);
          }
          this.form.get('to').setValue(null);
          this.form.get('cc').setValue(null);

          this.form.get('kindId').updateValueAndValidity();
          this.form.get('to').updateValueAndValidity();
        },
      });

    this.form
      .get('kindId')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (kindId: number) => {
          if (kindId) {
            this._fetchGroupUsers(kindId, true);
          }
        },
      });
  }

  private _initReplyForm() {
    this.form = this._formBuilder.group({
      content: [null, [Validators.required]],
    });
  }

  private _fetchMessages() {
    this.searchInput = null;
    const emailServiceMap = {
      [EmailType.PERSONAL]: {
        [EmailFolder.INBOX]: this._useEmailMessages.getAllPersonalInboxMessages,
        [EmailFolder.STARRED]: this._useEmailMessages.getAllPersonalStarredMessages,
        [EmailFolder.SENT]: this._useEmailMessages.getAllPersonalSentMessages,
        [EmailFolder.DRAFTS]: this._useEmailMessages.getAllPersonalDraftMessages,
        [EmailFolder.TRASH]: this._useEmailMessages.getAllPersonalTrashMessages,
        [EmailFolder.ALL_MAILS]: this._useEmailMessages.getAllPersonalAllMailMessages,
      },
      [EmailType.CLUB]: {
        [EmailFolder.INBOX]: this._useEmailMessages.getAllClubInboxMessages,
        [EmailFolder.STARRED]: this._useEmailMessages.getAllClubStarredMessages,
        [EmailFolder.SENT]: this._useEmailMessages.getAllClubSentMessages,
        [EmailFolder.DRAFTS]: this._useEmailMessages.getAllClubDraftMessages,
        [EmailFolder.TRASH]: this._useEmailMessages.getAllClubTrashMessages,
        [EmailFolder.ALL_MAILS]: this._useEmailMessages.getAllClubAllMailMessages,
      },
      [EmailType.GROUP]: {
        [EmailFolder.INBOX]: this._useEmailMessages.getAllGroupInboxMessages,
        [EmailFolder.STARRED]: this._useEmailMessages.getAllGroupStarredMessages,
        [EmailFolder.SENT]: this._useEmailMessages.getAllGroupSentMessages,
        [EmailFolder.DRAFTS]: this._useEmailMessages.getAllGroupDraftMessages,
        [EmailFolder.TRASH]: this._useEmailMessages.getAllGroupTrashMessages,
        [EmailFolder.ALL_MAILS]: this._useEmailMessages.getAllGroupAllMailMessages,
      },
    };

    const service$: Observable<any> = emailServiceMap[this.selectedEmailType]?.[this.selectedEmailFolder]?.call(this._useEmailMessages);

    if (service$) {
      service$.pipe(untilDestroyed(this)).subscribe({
        next: (messages: EmailMessageEntity[]) => {
          this.emailMessages = messages;
        },
        complete: () => {
          this.isLoading = false;
          this._processUsersForCCAndTO(this.emailMessages, this.teamUsers);
        },
        error: () => {
          this.isLoading = false;
          this._toastService.error(this._translate.instant('Something went wrong'));
        },
      });
    }
  }

  private _fetchTeamUsers() {
    this._useClub.getClubMembers().then((users) => {
      this.teamUsers = users;
      this.loadings.teamUsers = false;
    });
  }

  private _fetchGroups() {
    this._useGroup
      .getGroupsByUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (groups) => {
          this.groups = groups;
          this.loadings.groups = false;
        },
      });
  }

  private _fetchGroupUsers(groupId: number, mapUsersInForm = false) {
    this._useGroup
      .getApprovedGroupUsers(groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (group) => {
          this.groupParticipants = group.participants;

          if (mapUsersInForm) {
            this.form.get('to').setValue(group.participants.map((p) => p.groupUser[0].keycloakId));
          }

          if (this.selectedEmailMessage && this.selectedEmailMessage.group) {
            this._processGroupParticipantsIntoTo(this.selectedEmailMessage, this.groupParticipants);
          }
        },
      });
  }

  private _markMessageAsRead(message: EmailMessageEntity) {
    if (message.isRead === 0) {
      this._useEmailMessages.readMessage(message.id).subscribe({
        next: (res) => {
          if (res) {
            message.isRead = 1;
          }
        },
      });
    }
  }

  private _processUsersForCCAndTO(messages: EmailMessageEntity[], users: UserEntity[]) {
    if ((!messages && !messages.length) || !users || !users.length) return;

    messages.forEach((message) => {
      if (!message.cc || !message.to) return;
      message.ccUsers = message.cc.map((cc) => users.find((user) => user.keycloakId === cc));
      message.toUsers = message.to.map((to) => users.find((user) => user.keycloakId === to));
    });
  }

  private _processGroupParticipantsIntoTo(message: EmailMessageEntity, participants: GroupParticipant[]) {
    if (!message || !participants || !participants.length) return;
    message.toUsers = participants.map((p) => p.groupUser[0] as UserEntity);
  }

  private _getFileType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension) {
      if (extension === 'png' || extension === 'jpeg' || extension === 'jpg') {
        return 'image-file'; // If the file is an image
      } else if (extension === 'pdf') {
        return 'document-file'; // If the file is a PDF
      }
    }
    return 'document-file'; // Default to file icon if extension not matched
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    const urlTree = this._router.parseUrl(currentUrl);

    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigateByUrl(urlTree);
    });
  }
}
